import { BodyText, HeaderText } from "@/components/ui/Text";
import { cn } from "@/lib/utils";
import { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";

interface MarkdownRendererProps {
  content: string;
  compact?: boolean;
}

export const MarkdownRenderer = ({
  content,
  compact = false,
}: MarkdownRendererProps) => {
  const components: Components = useMemo(() => {
    return {
      // Map headings to our typography components
      // # = h1 -> HeaderText lg
      h1: ({ children }) => (
        <HeaderText tag="h1" size="lg" className="mb-2">
          {children}
        </HeaderText>
      ),
      // ## = h2 -> HeaderText md
      h2: ({ children }) => (
        <HeaderText tag="h2" size="md" className="mb-2">
          {children}
        </HeaderText>
      ),
      // ### = h3 -> HeaderText sm
      h3: ({ children }) => (
        <HeaderText tag="h3" size="sm">
          {children}
        </HeaderText>
      ),
      // Map paragraphs
      p: ({ children }) => (
        <BodyText tag="p" size="sm" className="mb-2">
          {children}
        </BodyText>
      ),
      // Map lists
      ul: ({ children }) => (
        <ul className={compact ? "list-disc pl-4 mb-1" : "list-disc pl-4 mb-2"}>
          {children}
        </ul>
      ),
      // Forward `start`/`type` so numbering is preserved even when the parser
      // splits a single list into multiple <ol> fragments (e.g. loose or
      // interrupted lists). Without this, the CSS counter resets to 1 on each
      // fragment, renumbering "1) 2) 3)" to "1) 1) 1)". See EHR-8168.
      ol: ({ children, start, type }) => (
        <ol
          start={start}
          type={type}
          className={
            compact ? "list-decimal pl-4 mb-1" : "list-decimal pl-4 mb-2"
          }
        >
          {children}
        </ol>
      ),
      li: ({ children }) => (
        <li className={compact ? "leading-snug" : "leading-none"}>
          <BodyText size="sm">{children}</BodyText>
        </li>
      ),
      // Map strong/bold text
      strong: ({ children }) => (
        <BodyText tag="span" size="sm" bold>
          {children}
        </BodyText>
      ),
      // Map emphasis/italic text
      em: ({ children }) => (
        <BodyText tag="span" size="sm" italic>
          {children}
        </BodyText>
      ),
    };
  }, [compact]);

  return (
    <div
      className={cn(
        compact &&
          "[&>p:has(+ul)]:mb-0 [&>p:has(+ol)]:mb-0 [&>p+ul]:mt-0 [&>p+ol]:mt-0",
      )}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  );
};
